import React, { FC } from 'react';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';
import { CTAWithTrailingText, DynamicComponent } from '@/components';
import {
  TypeComponentGridFields,
  TypeCtaWithTrailingTextFields,
  TypeLink_externalFields,
  TypeLink_internalFields,
} from '@/core/generated-types';
import { BackgroundColorWrapper } from '@/components/common/BackgroundColorWrapper';
import {
  CONTENT_TYPE_LINK,
  TW_COMPONENT_ROW_VERTICAL_PADDING_CLASSES,
  TW_COMPONENT_GRID_BOTTOM_PADDING_CLASSES,
  getContentType,
} from '@/core/constants';
import classNames from 'classnames';
import { Entry } from 'contentful';
import { UniversalCta } from '../UniversalCta';

export const DESKTOP_CLASSES: Record<string, string> = {
  '1': 'lg:grid-cols-1',
  '2': 'lg:grid-cols-2',
  '3': 'lg:grid-cols-3',
  '4': 'lg:grid-cols-4',
};

export const TABLET_CLASSES: Record<string, string> = {
  '1': 'md:grid-cols-1',
  '2': 'md:grid-cols-2',
  '3': 'md:grid-cols-3',
  '4': 'md:grid-cols-4',
};

export const MOBILE_CLASSES: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-2',
  '3': 'grid-cols-3',
  '4': 'grid-cols-4',
};

export interface ComponentGridProps extends TypeComponentGridFields {
  isHidden?: boolean | undefined;
  forceOneComponentPerRowOnTablet?: boolean;
  removeContainer?: boolean;
}

const ComponentGrid: FC<ComponentGridProps> = ({
  title,
  titleAsH1,
  headerAlignment,
  components,
  cta,
  backgroundColor,
  description,
  containerVerticalPadding = 0,
  removeHorizontalPadding,
  spacing,
  componentAlignment,
  removeContainer = false,
  wrapItems = true,
  mobileColCount,
  tabletColCount,
  desktopColCount,
  extraBottomPadding = 0,
}) => {
  if (!components?.length) {
    return null;
  }

  const visibleComponents = components.filter((c) => !c?.fields?.isHidden);
  if (visibleComponents?.length === 0) {
    return null;
  }

  const headingClasses = description ? 'mb-6' : 'mb-8';
  const hasOneComponent = visibleComponents.length === 1;
  const useGrid = !wrapItems && !hasOneComponent;
  const componentClasses =
    useGrid || hasOneComponent || !wrapItems
      ? 'w-full'
      : removeContainer
        ? 'lg:flex-1'
        : 'lg:w-[32%] w-full';

  let bodyClasses = wrapItems ? 'flex flex-wrap' : 'flex';

  if (useGrid) {
    const mobileColumns = !!mobileColCount
      ? MOBILE_CLASSES[mobileColCount]
      : 'grid-cols-1';
    const tabletColumns = !!tabletColCount
      ? TABLET_CLASSES[tabletColCount]
      : 'md:grid-cols-2';
    const desktopColumns = !!desktopColCount
      ? DESKTOP_CLASSES[desktopColCount]
      : DESKTOP_CLASSES[visibleComponents.length];
    bodyClasses = `grid ${mobileColumns} ${tabletColumns} ${desktopColumns}`;
  }

  return (
    <>
      {/* On a medium viewport, a preceding decorative divider component with a background color in the image shows a white gap between the image and this component when it uses a background color. A negative top margin covers this white gap on medium viewports. */}
      <BackgroundColorWrapper
        color={backgroundColor?.fields?.color}
        applyGutters
        classes={`${TW_COMPONENT_ROW_VERTICAL_PADDING_CLASSES[containerVerticalPadding]} md:-mt-1.5 ${TW_COMPONENT_GRID_BOTTOM_PADDING_CLASSES[extraBottomPadding]}`}
      >
        <section
          className={`${classNames(removeHorizontalPadding ? '' : 'px-6')}
          ${removeContainer ? '' : 'container mx-auto xl:max-w-6xl'}
        `}
        >
          {title && (
            <HeaderText
              size={titleAsH1 ? HeaderSize.H1 : HeaderSize.H2}
              text={title}
              align={headerAlignment}
              classes={classNames(
                headingClasses,
                headerAlignment === 'center' && 'title-container',
              )}
            />
          )}
          {description && (
            <p className="mx-auto mb-4 max-w-2xl pb-4 text-center leading-8">
              {description}
            </p>
          )}
          <div
            className={`${bodyClasses} ${
              componentAlignment === 'left'
                ? 'place-content-start'
                : 'place-content-center'
            } gap-${spacing}`}
          >
            {visibleComponents.map((component, i) => (
              <div
                key={`${component.sys.id}-${i}`}
                className={`${componentClasses} text-center`}
              >
                <DynamicComponent component={component} />
              </div>
            ))}
          </div>
          {cta && (
            <div className="mt-10 flex justify-center lg:mb-0 lg:mt-16">
              {getContentType(cta) === CONTENT_TYPE_LINK.INTERNAL ||
              getContentType(cta) === CONTENT_TYPE_LINK.EXTERNAL ? (
                <UniversalCta
                  link={
                    cta as Entry<
                      TypeLink_externalFields | TypeLink_internalFields
                    >
                  }
                  className="inline-flex w-full md:w-auto"
                />
              ) : getContentType(cta) ===
                CONTENT_TYPE_LINK.CTA_WITH_TRAILING_TEXT ? (
                <CTAWithTrailingText
                  {...(cta?.fields as TypeCtaWithTrailingTextFields)}
                />
              ) : (
                <DynamicComponent component={cta} />
              )}
            </div>
          )}
        </section>
      </BackgroundColorWrapper>
    </>
  );
};

export default React.memo(ComponentGrid);
